import ExcelJS from 'exceljs';
import React, { useEffect, useMemo, useState } from 'react';
import 'moment/locale/ko';
import moment from 'moment';
import styled from 'styled-components';
import Select from 'react-select';
import CustomTable from '../../../components/Table';
import SettlementController from '../../../controller/SettlementController';
import PartnerController, {
  PartnerListType,
  PartnerReportListType,
} from '../../../controller/PartnerController';
import { useNavigate } from 'react-router-dom';
import GuideTabContent from './storeReportGuide';
import DateTab from './DateTab';

const StoreReport = () => {
  const navigate = useNavigate();
  const userData = localStorage.getItem('user');
  const userType = userData ? JSON.parse(userData).user_type : null;
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const nowDate = new Date();
  nowDate.setHours(0, 0, 0, 0);
  // 월간 날짜 선택 함수
  const lastMonday = new Date(nowDate);
  lastMonday.setDate(nowDate.getDate() - ((nowDate.getDay() + 6) % 7));
  const possibleEndDate = new Date(lastMonday);
  possibleEndDate.setDate(lastMonday.getDate() + 6);
  // 주간 날짜 선택 함수
  const getLastCompleteWeekMonday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - ((today.getDay() + 6) % 7));

    const maxSelectableDate = new Date();
    maxSelectableDate.setDate(maxSelectableDate.getDate() - 6);

    return lastMonday <= maxSelectableDate
      ? lastMonday
      : new Date(lastMonday.setDate(lastMonday.getDate() - 7));
  };
  const initialStartDate = getLastCompleteWeekMonday();
  const initialEndDate = new Date(initialStartDate);
  initialEndDate.setDate(initialStartDate.getDate() + 6);
  const [salesId, setSalesId] = useState<any>(userData && JSON.parse(userData).sales_id);
  const [year, setYear] = useState<any>(oneMonthAgo.getFullYear());
  const [month, setMonth] = useState<number>(oneMonthAgo.getMonth() + 1);
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [oneDay, setOneDay] = useState<Date | null>(nowDate);
  const [companyList, setCompanyList] = useState<PartnerListType[]>([]);
  const [selectCompany, setSelectCompany] = useState<PartnerListType | null>();
  const [reportList, setReportList] = useState<PartnerReportListType[]>([]);
  const [tabIdx, setTabIdx] = useState<number>(0);
  const dateProps = {
    year,
    month,
    startDate,
    endDate,
    oneDay,
    setYear,
    setMonth,
    setStartDate,
    setEndDate,
    setOneDay,
    oneMonthAgo,
    setTabIdx,
  };
  
  const [sortedList, setSortedList] = useState<PartnerReportListType[]>([]);

  const handleCompanyChange = (selectedOption: PartnerListType | null) => {
    setSelectCompany(selectedOption);
    setSalesId(selectedOption?.sales_id);
  };

  const totalValues = useMemo(() => {
    const validReportList = Array.isArray(reportList) ? reportList : [];
    const totals = validReportList.reduce(
      (acc, data) => {
        acc.realOpenHour += data.real_open_hour || 0;
        acc.registOpenHour += Number(data.regist_open_hour);
        acc.orderCount += Number(data.order_count);
        acc.cancelCount += Number(data.cancel_count);
        acc.completeOrderCount += Number(data.complete_order_count);
        acc.lateOrderCount += Number(data.late_order_count);
        if (tabIdx === 0) {
          acc.settlementAmount += Number(data.settlement_amount);
          acc.paymentAmount += Number(data.payment_amount);
          acc.entryFee += Number(data.entry_fee);
          acc.firstMaint += Number(data.first_maint);
          acc.secondMaint += Number(data.second_maint);
          acc.thirdMaint += Number(data.third_maint);
          acc.prepaidAmount += Number(data.prepaid_amount);
          acc.monthPaidAmount += Number(data.month_paid_amount);
        }

        return acc;
      },
      {
        realOpenHour: 0,
        registOpenHour: 0,
        orderCount: 0,
        cancelCount: 0,
        completeOrderCount: 0,
        lateOrderCount: 0,
        settlementAmount: 0,
        paymentAmount: 0,
        entryFee: 0,
        firstMaint: 0,
        secondMaint: 0,
        thirdMaint: 0,
        prepaidAmount: 0,
        monthPaidAmount: 0,
      },
    );

    return totals;
  }, [reportList, tabIdx]);
  const {
    realOpenHour,
    registOpenHour,
    orderCount,
    cancelCount,
    completeOrderCount,
    lateOrderCount,
    settlementAmount,
    paymentAmount,
    entryFee,
    firstMaint,
    secondMaint,
    thirdMaint,
    prepaidAmount,
    monthPaidAmount,
  } = totalValues;
  const excelHeader = [
    '순서',
    '매장ID',
    '영업사명',
    '매장명',
    '회원가입일',
    '온보딩 완료일',
    '온보딩 소요일',
    '실제 운영한\n영업시간',
    '등록된\n영업시간',
    '정상 운영 비율',
    '총 주문건수',
    '주문 거절 건수\n(자동 취소 포함)',
    '배달 완료\n주문건수',
    '주문수행률',
    '주문지연\n알림 건수',
    '접수 평균 시간',
    '조리 완료\n수행률',
    '정산 대상 금액',
    '(A)인센티브\n지급액',
    '입점대행료\n지급액',
    '유지장려금1',
    '유지장려금2',
    '유지장려금3',
    '기지급액',
    '총 정산\n완료 여부',
    '(B)이번달 지급\n대상 금액',
    '(A+B)\n월 지급액',
    '정산 거절 사유',
  ];

  const sortedHeader = [
    '',
    '',
    '',
    '',
    'store_created_at',
    'store_open_date',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
  ];

  const filteredExcelHeader = tabIdx !== 0 ? excelHeader.slice(0, 17) : excelHeader;

  const renderTableItem = () => {
    return (
      <>
        {sortedList &&
          sortedList.length > 0 &&
          sortedList.map((data: PartnerReportListType, dataIndex: number) => (
            <tr
              className={`dataTableWrap ${data.is_pay_fulfilled === 0 && 'errorStore'}`}
              key={dataIndex}>
              <td>{dataIndex + 1}</td>
              <td>{data.store_id}</td>
              <td>{data.sales_name}</td>
              <td>{data.store_name}</td>
              <td>{moment(data.store_created_at).format('YYYY-MM-DD')}</td>
              <td>
                {data.store_open_date ? (
                  moment(data.store_open_date).format('YYYY-MM-DD')
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              <td>
                {data.store_open_date ? (
                  moment(data.store_open_date)
                    .startOf('day')
                    .diff(moment(data.store_created_at).startOf('day'), 'days') + '일'
                ) : (
                  <p className="checkDate">
                    {moment()
                      .startOf('day')
                      .diff(moment(data.store_created_at).startOf('day'), 'days')}
                    일
                  </p>
                )}
              </td>
              <td>
                {data.real_open_hour !== null ? data.real_open_hour : <p className="nullText">-</p>}
              </td>
              <td>
                {data.regist_open_hour !== null ? (
                  data.regist_open_hour
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              {/* 정상운영비율 */}
              <td>
                {data.open_rate !== null ? (
                  Math.round(data.open_rate) + '%'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              {/* 총주문건수 */}
              <td>
                {data.order_count !== null ? (
                  data.order_count + '건'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              <td>
                {data.cancel_count !== null ? (
                  data.cancel_count + '건'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              <td>
                {data.complete_order_count !== null ? (
                  data.complete_order_count + '건'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              {/* 주문수행률 */}
              <td>
                {data.complete_order_rate !== null ? (
                  Math.round(data.complete_order_rate) + '%'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              <td>
                {data.late_order_count !== null ? (
                  data.late_order_count + '건'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>

              <td>
                {data.confirmed_avg_sec !== null ? (
                  data.confirmed_avg_sec + '초'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              <td>
                {data.cook_complete_rate !== null ? (
                  Math.round(data.cook_complete_rate) + '%'
                ) : (
                  <p className="nullText">-</p>
                )}
              </td>
              {/* 주간/일간 조회일때는 보이지 않음 */}
              {tabIdx === 0 && (
                <>
                  {/* 정산대상금액 */}
                  <td>
                    {data.settlement_amount !== null ? (
                      Number(data.settlement_amount).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>
                  {/* (A)인센티브 지급액 */}
                  <td>
                    {data.payment_amount !== null ? (
                      Number(data.payment_amount).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>
                  {/* 입점대행료 */}
                  <td>
                    {data.entry_fee !== null ? (
                      Number(data.entry_fee).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>
                  {/* 유지보수비1 */}
                  <td>
                    {data.first_maint !== null ? (
                      Number(data.first_maint).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>
                  {/* 유지보수비2 */}
                  <td>
                    {data.second_maint !== null ? (
                      Number(data.second_maint).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>

                  {/* 유지보수비3 */}
                  <td>
                    {data.third_maint !== null ? (
                      Number(data.third_maint).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>

                  {/* 기지급액 */}
                  <td>
                    {data.prepaid_amount !== null ? (
                      Number(data.prepaid_amount).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>
                  <td>{data.is_all_paid === 1 ? '정산 완료' : <p className="nullText">-</p>}</td>
                  {/* (B)이번달 지급 대상 금액 */}
                  <td>
                    {data.month_paid_amount !== null ? (
                      Number(data.month_paid_amount).toLocaleString() + '원'
                    ) : (
                      <p className="nullText">-</p>
                    )}
                  </td>
                  <td>
                    {(
                      Number(data.payment_amount) + Number(data.month_paid_amount)
                    ).toLocaleString() + '원'}
                  </td>

                  <td>{data.is_pay_fulfilled === 0 && data.reason}</td>
                </>
              )}
            </tr>
          ))}
        {reportList.length > 0 ? (
          <tr className="totalTableWrap">
            <td colSpan={7}>합계</td>
            <td>{realOpenHour.toLocaleString()}시간</td>
            <td>{registOpenHour.toLocaleString()}시간</td>
            <td>-</td>
            <td>{orderCount.toLocaleString()}건</td>
            <td>{cancelCount.toLocaleString()}건</td>
            <td>{completeOrderCount.toLocaleString()}건</td>
            <td>-</td>
            <td>{lateOrderCount.toLocaleString()}건</td>
            <td>-</td>
            <td>-</td>
            {tabIdx === 0 && (
              <>
                <td>{settlementAmount.toLocaleString()}원</td>
                <td>{paymentAmount.toLocaleString()}원</td>
                <td>{entryFee.toLocaleString()}원</td>
                <td>{firstMaint.toLocaleString()}원</td>
                <td>{secondMaint.toLocaleString()}원</td>
                <td>{thirdMaint.toLocaleString()}원</td>
                <td>{prepaidAmount.toLocaleString()}원</td>
                <td>-</td>
                <td>{monthPaidAmount.toLocaleString()}원</td>
                <td>{(paymentAmount + monthPaidAmount).toLocaleString()}원</td>
                <td>-</td>
              </>
            )}
          </tr>
        ) : (
          <p className="noneData">데이터 없음</p>
        )}
      </>
    );
  };

  const excelDownloadClick = () => {
    if (reportList) {
      let excelReportList = reportList;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet 1');

      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell, colNumber) => {
          cell.font = {
            name: 'Calibri',
            size: 12,
          };
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'DB576D' } },
            left: { style: 'thin', color: { argb: 'DB576D' } },
            bottom: { style: 'thin', color: { argb: 'DB576D' } },
            right: { style: 'thin', color: { argb: 'DB576D' } },
          };
          if (colNumber === 16) {
            cell.border.left = { style: 'thick', color: { argb: 'DB576D' } };
          }
        });
      });
      worksheet.addRow(filteredExcelHeader.slice(0, filteredExcelHeader.length));

      for (let i = 0; i < excelReportList.length; i++) {
        worksheet.addRow([
          i + 1,
          excelReportList[i].store_id !== null ? Number(excelReportList[i].store_id) : '',
          excelReportList[i].sales_name !== null ? excelReportList[i].sales_name : '',
          excelReportList[i].store_name !== null ? excelReportList[i].store_name : '',
          excelReportList[i].store_created_at !== null
            ? moment(excelReportList[i].store_created_at).format('YYYY-MM-DD')
            : '',
          excelReportList[i].store_open_date !== null
            ? moment(excelReportList[i].store_open_date).format('YYYY-MM-DD')
            : '',
          excelReportList[i].store_open_date
            ? moment(excelReportList[i].store_open_date)
                .startOf('day')
                .diff(moment(excelReportList[i].store_created_at).startOf('day'), 'days')
            : moment()
                .startOf('day')
                .diff(moment(excelReportList[i].store_created_at).startOf('day'), 'days'),
          excelReportList[i].real_open_hour !== null ? excelReportList[i].real_open_hour : '',
          excelReportList[i].regist_open_hour !== null ? excelReportList[i].regist_open_hour : '',
          excelReportList[i].open_rate !== null ? Math.round(excelReportList[i].open_rate) : '',
          excelReportList[i].order_count !== null ? excelReportList[i].order_count : '',
          excelReportList[i].cancel_count !== null ? excelReportList[i].cancel_count : '',
          excelReportList[i].complete_order_count !== null
            ? excelReportList[i].complete_order_count
            : '',
          excelReportList[i].complete_order_rate !== null
            ? Math.round(excelReportList[i].complete_order_rate)
            : '',
          tabIdx === 0 && excelReportList[i].late_order_count !== null
            ? excelReportList[i].late_order_count
            : '',
          excelReportList[i].confirmed_avg_sec !== null ? excelReportList[i].confirmed_avg_sec : '',
          excelReportList[i].cook_complete_rate !== null
            ? Math.round(excelReportList[i].cook_complete_rate)
            : '',
          tabIdx === 0 && excelReportList[i].settlement_amount !== null
            ? Number(excelReportList[i].settlement_amount)
            : '',
          tabIdx === 0 && excelReportList[i].payment_amount !== null
            ? Number(excelReportList[i].payment_amount)
            : '',
          tabIdx === 0 && excelReportList[i].entry_fee !== null
            ? Number(excelReportList[i].entry_fee)
            : '',
          tabIdx === 0 && excelReportList[i].first_maint !== null
            ? Number(excelReportList[i].first_maint)
            : '',
          tabIdx === 0 && excelReportList[i].second_maint !== null
            ? Number(excelReportList[i].second_maint)
            : '',
          tabIdx === 0 && excelReportList[i].third_maint !== null
            ? Number(excelReportList[i].third_maint)
            : '',
          tabIdx === 0 && excelReportList[i].prepaid_amount !== null
            ? Number(excelReportList[i].prepaid_amount)
            : '',
          tabIdx === 0 && excelReportList[i].is_all_paid === 1
            ? '정산 완료'
            : tabIdx === 0
            ? '미정산'
            : '',
          tabIdx === 0 && excelReportList[i].month_paid_amount !== null
            ? Number(excelReportList[i].month_paid_amount)
            : '',
          tabIdx === 0
            ? Number(excelReportList[i].payment_amount) +
              Number(excelReportList[i].month_paid_amount)
            : '',
          tabIdx === 0 && excelReportList[i].is_pay_fulfilled === 0
            ? excelReportList[i].reason
            : '',
        ]);
      }

      // 아래 총합계
      const lastRowIndex = excelReportList.length + 2;
      worksheet.getCell(`A${lastRowIndex}`).value = '합계';
      worksheet.getCell(`H${lastRowIndex}`).value = realOpenHour;
      worksheet.getCell(`I${lastRowIndex}`).value = registOpenHour;
      worksheet.getCell(`K${lastRowIndex}`).value = orderCount;
      worksheet.getCell(`L${lastRowIndex}`).value = cancelCount;
      worksheet.getCell(`M${lastRowIndex}`).value = completeOrderCount;
      worksheet.getCell(`O${lastRowIndex}`).value = lateOrderCount;
      if (tabIdx === 0) {
        worksheet.getCell(`R${lastRowIndex}`).value = settlementAmount;
        worksheet.getCell(`S${lastRowIndex}`).value = paymentAmount;
        worksheet.getCell(`T${lastRowIndex}`).value = entryFee;
        worksheet.getCell(`U${lastRowIndex}`).value = firstMaint;
        worksheet.getCell(`V${lastRowIndex}`).value = secondMaint;
        worksheet.getCell(`W${lastRowIndex}`).value = thirdMaint;
        worksheet.getCell(`X${lastRowIndex}`).value = prepaidAmount;
        worksheet.getCell(`Z${lastRowIndex}`).value = monthPaidAmount;
        worksheet.getCell(`AA${lastRowIndex}`).value = paymentAmount + monthPaidAmount;
      }

      // 엑셀 파일로 저장
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `노크노크 매장/영업사 리포트.xlsx`);
        document.body.appendChild(link);
        link.click();
      });
    }
  };
  const searchReportButton = () => {
    if (tabIdx === 0) {
      getMonthReport();
    } else if (tabIdx === 1) {
      getWeekReport();
    } else if (tabIdx === 2) {
      getDayReport();
    }
  };

  // API 호출
  const getUsersBySales = async () => {
    try {
      const response = await PartnerController.getUsersBySales();

      if (response && response.data) {
        const salesData = response.data.data.users_by_sales;
        const formattedPartnerist = salesData?.map((item: PartnerListType) => ({
          value: item.sales_id,
          label: item.sales_name,
          sales_id: item.sales_id,
          users: item.users,
        }));
        setCompanyList(formattedPartnerist);
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };
  const getMonthReport = async () => {
    try {
      const oData = {
        year: year,
        month: month,
        sales_id: salesId,
      };
      const response = await SettlementController.getMonthReport(oData);

      if (response && response.data) {
        setReportList(response.data.data);
        setSortedList(response.data.data);
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };
  const getWeekReport = async () => {
    try {
      const oData = {
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        sales_id: salesId,
      };
      const response = await SettlementController.getWeekReport(oData);

      if (response && response.data) {
        setReportList(response.data.data);
        setSortedList(response.data.data);
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };
  const getDayReport = async () => {
    try {
      const oData = {
        date: moment(oneDay).format('YYYY-MM-DD'),
        sales_id: salesId,
      };
      const response = await SettlementController.getDayReport(oData);

      if (response && response.data) {
        setReportList(response.data.data);
        setSortedList(response.data.data);
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };

  useEffect(() => {
    if (userType === 0) {
      getUsersBySales();
    }
  }, []);
  useEffect(() => {
    if (companyList && companyList.length > 0) {
      setSelectCompany(companyList[0]);
    }
  }, [companyList]);
  useEffect(() => {
    getMonthReport();
  }, [year, month]);

  return (
    <Style>
      <div className="rightContentWrap">
        <button className="backButton" onClick={() => navigate('/dashboard')}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_home.svg`} alt="뒤로가기버튼" />
        </button>
        <h2>매장/영업사 리포트</h2>
        <div className="searchWrap">
          {userType === 0 && (
            <Select options={companyList} value={selectCompany} onChange={handleCompanyChange} />
          )}
          <DateTab {...dateProps} />
          <button onClick={searchReportButton} className="searchButton">
            조회
          </button>
        </div>
        <div className="buttonsWrap">
          <button onClick={excelDownloadClick} className="excelButton">
            액셀 다운로드
          </button>
          <GuideTabContent />
        </div>
        {reportList && (
          <div style={{ flexShrink: 0 }}>
            <CustomTable
              tableHeader={filteredExcelHeader}
              renderTableItem={renderTableItem}
              type="border"
              sortedHeader={sortedHeader}
              dataList={reportList}
              setSortedList={setSortedList}
            />
          </div>
        )}
      </div>
    </Style>
  );
};

export default StoreReport;

const Style = styled.div`
  p {
    padding: 0;
    margin: 0;
  }

  .rightContentWrap {
    min-width: 100%;
    width: fit-content;
    min-height: 100vh;
    background: #ffffff;
    padding: 2%;
  }

  /* searchWrap */
  .searchWrap {
    display: flex;
    align-items: center;
    /* justify-content: stretch; */
    gap: 10px;
    margin-top: 30px;
    margin-bottom: 20px;

    .css-b62m3t-container {
      width: 400px;
    }
    input {
      width: 150px;
      padding: 9px 10px;
      border-radius: 4px;
      border: 1px solid hsl(0, 0%, 70%);
      outline: none;
    }
    .searchButton {
      margin-left: 10px;
      padding: 9px 20px;
      background: #000;
      color: #fff;
      border-radius: 4px;
      border: none;
      outline: none;
    }
  }
  /* buttonsWrap */
  .buttonsWrap {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    align-items: flex-start;

    button {
      padding: 9px 20px;
      border-radius: 4px;
      border: none;
      outline: none;
      background: #e7e7e7;
      margin-right: 4px;
    }
    .excelButton {
      background: #40977f;
      color: #fff;
    }
  }

  /* datepicker */
  .react-datepicker__month-text {
    padding: 6px 0 4px 0;
  }

  /* table */

  .normalTable thead tr {
    font-size: 14px;
    line-height: 18px;
    white-space: nowrap;
  }
  .normalTable tbody td {
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    text-align: center;

    img {
      width: 16px;
      cursor: pointer;
    }
  }
  .dataTableWrap.errorStore {
    background: #fff4f4;
  }
  .normalTable th:nth-child(19),
  .dataTableWrap td:nth-child(19) {
    border-right: thick double #bbb;
  }
  .normalTable th:nth-child(25),
  .dataTableWrap td:nth-child(25) {
    border-right: thick double #bbb;
  }
  .totalTableWrap td:nth-child(13),
  .totalTableWrap td:nth-child(19) {
    border-right: thick double #bbb;
  }
  .totalTableWrap td {
    background: #ddd;
  }

  .nullText {
    color: #999;
  }
  thead {
    position: sticky;
    top: -1px;
  }

  /* guideWrap */
  .guideWrap {
    z-index: 2;
    margin-top: 10px;
    margin-bottom: 10px;
    padding: 10px 20px;
    border-radius: 5px;
    border: 1px solid #cecece;
    background: #fcfcfc;
    position: absolute;
    left: 2vw;

    h3 {
      color: #979797;
      margin: 10px 0 4px 0;
      font-size: 14px;
      font-weight: 500;
    }
    p {
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
    }
    .pointText p {
      font-weight: 500;
      color: #ff6060;
    }
  }
  .noneData {
    padding: 10px 16px;
    min-width: 100%;
    text-align: center;
    white-space: nowrap;
  }
  .checkDate {
    color: #ff6060;
  }

  @media (max-width: 1400px) {
    .totalTable {
      width: 50%;
    }
  }
`;
